import type { PermissionLevel } from '@datapad/data-model';
import { Person, Security, Shield, Visibility } from '@mui/icons-material';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { menuLabelSx, menuLabelTypographyVariant, menuSectionSx } from './Constants.js';

/**
 * {@link ViewAsSelector} input props.
 */
export interface ViewAsSelectorProps {
	/**
	 * The current effective permission level being simulated.
	 */
	currentEffectiveLevel: PermissionLevel;

	/**
	 * Callback when the view mode changes.
	 * Pass `undefined` to reset to actual Game Master permissions.
	 */
	onChange(level: PermissionLevel | undefined): void;
}

/**
 * GM-only selector for "View As" mode.
 *
 * @remarks
 * Allows Game Masters to simulate viewing the app as a player or guest.
 * This affects both UI visibility and data filtering throughout the app.
 *
 * The toggle persists only for the current session and resets on page refresh.
 */
export function ViewAsSelector(props: ViewAsSelectorProps): React.ReactElement {
	const { currentEffectiveLevel, onChange } = props;

	const handleChange = React.useCallback(
		(_event: React.MouseEvent<HTMLElement>, newValue: PermissionLevel | null) => {
			// If same button clicked (null) or GM selected, reset to actual permissions
			if (newValue === null || newValue === 'game-master') {
				onChange(undefined);
			} else {
				onChange(newValue);
			}
		},
		[onChange],
	);

	return (
		<Box sx={menuSectionSx}>
			<Typography variant={menuLabelTypographyVariant} sx={menuLabelSx}>
				<Visibility sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
				View As
			</Typography>

			<ToggleButtonGroup
				value={currentEffectiveLevel}
				exclusive
				onChange={handleChange}
				aria-label="View as permission level"
				size="small"
				fullWidth
				sx={(theme) => ({
					'& .MuiToggleButton-root': {
						textTransform: 'none',
						py: 0.75,
						fontSize: '0.8125rem',
						/*
						 * Selected state uses primary color with transparency
						 * for a subtle but clear indication.
						 */
						'&.Mui-selected': {
							bgcolor: alpha(theme.palette.primary.main, 0.15),
							color: theme.palette.primary.main,
							'&:hover': {
								bgcolor: alpha(theme.palette.primary.main, 0.25),
							},
						},
					},
				})}
			>
				<ToggleButton value="game-master" aria-label="Game Master">
					<Tooltip title="View as Game Master (full access)">
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Security fontSize="small" />
							GM
						</Box>
					</Tooltip>
				</ToggleButton>
				<ToggleButton value="player" aria-label="Player">
					<Tooltip title="View as Player (limited to known contacts)">
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Shield fontSize="small" />
							Player
						</Box>
					</Tooltip>
				</ToggleButton>
				<ToggleButton value="guest" aria-label="Guest">
					<Tooltip title="View as Guest (read-only, famous contacts only)">
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Person fontSize="small" />
							Guest
						</Box>
					</Tooltip>
				</ToggleButton>
			</ToggleButtonGroup>
		</Box>
	);
}
